/**
 * SentReviewsTab — operator-side tracking for client magic-link reviews.
 * Lives inside `PostsPage` as a sibling tab to the post list.
 *
 * Pairs with backend:
 *   GET    /api/reviews/sent              → list every review_link the operator sent
 *   POST   /api/reviews/{id}/cancel       → retract a still-pending link
 *   POST   /api/reviews/send              → resend (creates a fresh token; old one stays canceled)
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  ClipboardList, Copy, Loader2, RefreshCw, XCircle, Clock, CheckCircle2, MessageSquare,
  ExternalLink, AlertCircle, Search,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-amber-100 text-amber-800 border-amber-200' },
  approved: { label: 'Approved', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  changes_requested: { label: 'Changes requested', icon: AlertCircle, color: 'bg-orange-100 text-orange-800 border-orange-200' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'bg-rose-100 text-rose-800 border-rose-200' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
};

const formatRelative = (iso) => {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

export const SentReviewsTab = () => {
  const { api } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState({}); // {[review_id]: 'cancelling' | 'copying'}

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get('/reviews/sent');
      setReviews(r.data?.reviews || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load sent reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => {
    const out = { all: reviews.length, pending: 0, approved: 0, changes_requested: 0, rejected: 0, cancelled: 0 };
    for (const r of reviews) out[r.status] = (out[r.status] || 0) + 1;
    return out;
  }, [reviews]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reviews
      .filter((r) => statusFilter === 'all' ? true : r.status === statusFilter)
      .filter((r) => {
        if (!q) return true;
        return (
          (r.client_name || '').toLowerCase().includes(q) ||
          (r.first_post_title || '').toLowerCase().includes(q) ||
          (r.recipient_email || '').toLowerCase().includes(q)
        );
      });
  }, [reviews, statusFilter, search]);

  const copyLink = async (review) => {
    setBusy((b) => ({ ...b, [review.id]: 'copying' }));
    try {
      await navigator.clipboard.writeText(review.review_url);
      toast.success('Review link copied');
    } catch {
      toast.error('Could not copy — select & copy manually');
    } finally {
      setBusy((b) => { const { [review.id]: _, ...rest } = b; return rest; });
    }
  };

  const cancelReview = async (review) => {
    if (!window.confirm(`Cancel this pending review for ${review.client_name || review.recipient_email || 'this client'}?\nThe magic link will stop working immediately.`)) {
      return;
    }
    setBusy((b) => ({ ...b, [review.id]: 'cancelling' }));
    try {
      await api.post(`/reviews/${review.id}/cancel`);
      toast.success('Review cancelled — link is now dead');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    } finally {
      setBusy((b) => { const { [review.id]: _, ...rest } = b; return rest; });
    }
  };

  const StatusFilterPill = ({ value, label, n, tone = 'zinc' }) => {
    const active = statusFilter === value;
    const colorClass = {
      zinc:    active ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300',
      amber:   active ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-700 border-amber-200 hover:border-amber-300',
      emerald: active ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-200 hover:border-emerald-300',
      orange:  active ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-orange-700 border-orange-200 hover:border-orange-300',
      rose:    active ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-rose-700 border-rose-200 hover:border-rose-300',
    }[tone];
    return (
      <button
        type="button"
        onClick={() => setStatusFilter(value)}
        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${colorClass}`}
        data-testid={`sent-reviews-filter-${value}`}
      >
        {label}
        <span className={`ml-1.5 ${active ? 'opacity-80' : 'opacity-60'}`}>({n})</span>
      </button>
    );
  };

  return (
    <div className="space-y-4" data-testid="sent-reviews-tab">
      {/* Summary + filter chips */}
      <div className="flex flex-wrap gap-2">
        <StatusFilterPill value="all" label="All" n={counts.all} tone="zinc" />
        <StatusFilterPill value="pending" label="Pending" n={counts.pending} tone="amber" />
        <StatusFilterPill value="approved" label="Approved" n={counts.approved} tone="emerald" />
        <StatusFilterPill value="changes_requested" label="Changes" n={counts.changes_requested} tone="orange" />
        <StatusFilterPill value="rejected" label="Rejected" n={counts.rejected} tone="rose" />
        {counts.cancelled > 0 && <StatusFilterPill value="cancelled" label="Cancelled" n={counts.cancelled} tone="zinc" />}
        <div className="flex-1 min-w-[180px] sm:max-w-xs relative">
          <Search className="w-3.5 h-3.5 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search client / email / title…"
            className="pl-8 h-9 text-sm"
            data-testid="sent-reviews-search"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={loading}
          data-testid="sent-reviews-refresh"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-zinc-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {!loading && visible.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center space-y-2">
            <ClipboardList className="w-10 h-10 text-zinc-300 mx-auto" />
            <p className="font-medium text-zinc-700">
              {reviews.length === 0 ? "You haven't sent any client reviews yet" : 'No reviews match this filter'}
            </p>
            <p className="text-xs text-zinc-500 max-w-md mx-auto">
              {reviews.length === 0
                ? "Pick a draft post, click 'Send for client review', and the magic-link status will appear here."
                : "Try a different status filter or clear the search."}
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && visible.length > 0 && (
        <div className="space-y-2">
          {visible.map((review) => {
            const meta = STATUS_META[review.status] || STATUS_META.pending;
            const Icon = meta.icon;
            const isPending = review.status === 'pending';
            const showFeedback =
              review.client_feedback &&
              ['rejected', 'changes_requested', 'approved'].includes(review.status);
            return (
              <Card key={review.id} className="hover:border-zinc-300 transition-colors" data-testid={`sent-review-row-${review.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`gap-1 ${meta.color}`} data-testid={`sent-review-status-${review.id}`}>
                          <Icon className="w-3 h-3" />
                          {meta.label}
                        </Badge>
                        {review.post_count > 1 && (
                          <Badge variant="secondary" className="text-[10px]">{review.post_count} posts</Badge>
                        )}
                        <span className="text-xs text-zinc-500">
                          Sent {formatRelative(review.created_at)}
                          {review.responded_at && ` · Responded ${formatRelative(review.responded_at)}`}
                        </span>
                      </div>
                      <p className="font-semibold text-zinc-900 mt-1.5 truncate" title={review.first_post_title}>
                        {review.first_post_title}
                      </p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        Sent to <span className="font-mono text-zinc-700">{review.recipient_email || '(no email)'}</span>
                        {review.client_name && <> · <span className="text-zinc-700">{review.client_name}</span></>}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1.5 shrink-0">
                      {isPending && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyLink(review)}
                            disabled={busy[review.id] === 'copying'}
                            data-testid={`sent-review-copy-${review.id}`}
                          >
                            <Copy className="w-3.5 h-3.5 mr-1" />
                            Copy link
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => cancelReview(review)}
                            disabled={busy[review.id] === 'cancelling'}
                            className="text-rose-600 hover:bg-rose-50"
                            data-testid={`sent-review-cancel-${review.id}`}
                          >
                            {busy[review.id] === 'cancelling'
                              ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                              : <XCircle className="w-3.5 h-3.5 mr-1" />}
                            Cancel
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(review.review_url, '_blank', 'noopener')}
                        title="Open client-facing view"
                        data-testid={`sent-review-view-${review.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                  {showFeedback && (
                    <div className="bg-zinc-50 border border-zinc-200 rounded-md p-2.5">
                      <p className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1 flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        Client feedback
                      </p>
                      <p className="text-sm text-zinc-700 whitespace-pre-line">{review.client_feedback}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SentReviewsTab;
